import { ALL_ITEMS } from '../data/rubric';
import { STATUS_ORDER } from '../data/constants';
import { GOALS } from '../data/goals';
import { ROOT_CAUSES } from '../data/root-causes';
import { uid } from './format';
import type { Audit, CriterionState, CriterionStatus, FigureCategory, FlatRubricItem, Verdict } from '../types';
import type { RootCause } from '../data/root-causes';

/** Reset an audit's include-map to the current goal's defaults (optional → off). */
export function applyGoalDefaults(a: Audit): void {
  const g = GOALS[a.goal] ?? GOALS.subscriber_campaign;
  a.include = a.include || {};
  ALL_ITEMS.forEach((i) => {
    a.include[i.id] = g.map[i.id] !== 'optional';
  });
}

/** 1-based index of a figure within an audit (matches "Fig. N" labels). */
export function figNum(a: Audit, figId: string): number | '?' {
  const i = a.figures.findIndex((f) => f.id === figId);
  return i < 0 ? '?' : i + 1;
}

/** Figures whose category is one of `cats`. */
export function figsByCat(a: Audit, cats: FigureCategory[]) {
  return a.figures.filter((f) => cats.includes(f.category));
}

/** Roll a rubric category up to its most-severe criterion status. */
export function catStatus(a: Audit, catLetter: string): CriterionStatus {
  const items = ALL_ITEMS.filter((i) => i.catLetter === catLetter);
  for (const s of STATUS_ORDER) {
    if (items.some((i) => a.criteria[i.id]?.status === s)) return s;
  }
  return 'na';
}

/** Auto verdict from the criteria, unless the auditor pinned one. */
export function computeVerdict(a: Audit): Exclude<Verdict, 'auto'> {
  if (a.verdict !== 'auto') return a.verdict;
  const critical = ALL_ITEMS.filter((i) => a.criteria[i.id]?.status === 'critical').length;
  const assessed = ALL_ITEMS.filter((i) => a.criteria[i.id]?.status !== 'na').length;
  if (assessed === 0) return 'fixes';
  if (critical === 0) return 'ready';
  return 'fixes';
}

/** Count of criteria that have been assessed (not N/A). */
export function assessedCount(a: Audit): number {
  return ALL_ITEMS.filter((i) => a.criteria[i.id]?.status !== 'na').length;
}

/** Count of criteria at a given status. */
export function statusCount(a: Audit, status: CriterionStatus): number {
  return ALL_ITEMS.filter((i) => a.criteria[i.id]?.status === status).length;
}

/* ---------- Root-cause dedup ---------- */

export type Severity = 'must' | 'should' | 'nice';
const SEV_RANK: Record<Severity, number> = { must: 3, should: 2, nice: 1 };

/** Map a criterion status → severity bucket (null for pass/na). */
export function statusToSev(s: CriterionStatus): Severity | null {
  return s === 'critical' ? 'must' : s === 'conversion' ? 'should' : s === 'housekeeping' ? 'nice' : null;
}

export interface RootCauseMember {
  id: string;
  cr: CriterionState;
  it: FlatRubricItem;
  sev: Severity;
}
export interface RootCauseResult {
  rc: RootCause;
  members: RootCauseMember[];
  priority: Severity;
}

/**
 * Collapse included, non-passing findings into root causes with a
 * computed priority (max member severity), sorted must → should → nice.
 * Mirrors the prototype's computeRootCauses().
 */
export function computeRootCauses(a: Audit): RootCauseResult[] {
  const out: RootCauseResult[] = [];
  ROOT_CAUSES.forEach((rc) => {
    const members = rc.members
      .filter((id) => a.include[id] && a.criteria[id] && statusToSev(a.criteria[id]!.status))
      .map((id) => ({
        id,
        cr: a.criteria[id]!,
        it: ALL_ITEMS.find((x) => x.id === id)!,
        sev: statusToSev(a.criteria[id]!.status)!,
      }));
    if (!members.length) return;
    const priority = members.reduce<Severity>((mx, m) => (SEV_RANK[m.sev] > SEV_RANK[mx] ? m.sev : mx), 'nice');
    out.push({ rc, members, priority });
  });
  out.sort((x, y) => SEV_RANK[y.priority] - SEV_RANK[x.priority]);
  return out;
}

/**
 * A fully-populated example audit — the "Load demo channel" fixture.
 * Mirrors the prototype's Grace & Lace walkthrough.
 */
export function buildDemoAudit(base: Audit): Audit {
  const a = base;
  a.name = 'Grace & Lace';
  a.handle = '@graceandlace';
  a.tier = 'presentation';
  a.stats = { subs: '18.6K', videos: '214', views: '1.9M' };
  a.trailer = 'no';
  a.cadence = 'Jun 26, Jun 12, May 30, May 9, Apr 28, Apr 3, Mar 19 …';
  a.summary =
    'Grace & Lace has real brand equity — the banner reads clearly and its three most-viewed videos share a strong, high-contrast thumbnail style. But the channel currently has no trailer for non-subscribers, and its most recent uploads have drifted to a low-contrast style that no longer matches the videos that actually perform. Before a subscriber campaign, the channel needs a trailer and a thumbnail refresh that returns to the winning pattern; everything else is secondary.';
  a.verdict = 'fixes';

  const set = (id: string, status: CriterionStatus, finding: string, action: string): void => {
    a.criteria[id] = { status, finding, action, figs: [] };
  };
  set(
    'A1',
    'ok',
    'The banner and About page make the channel’s purpose clear within a couple of seconds — faith-centred lifestyle content aimed at a young Christian audience.',
    'No change needed.',
  );
  set(
    'B1',
    'critical',
    'There is no channel trailer for non-subscribed visitors. For a subscriber campaign, the landing surface currently has no dedicated "why subscribe" pitch — paid traffic arrives with nothing framing the ask.',
    'Produce at least one channel trailer, ideally three variants for A/B testing during the campaign.',
  );
  set(
    'C1',
    'conversion',
    'Across the recent uploads the thumbnail style is inconsistent — some use bold dark-outlined text, others thin white text on light photography, so the grid reads as unbranded to a browsing visitor.',
    'Establish a single thumbnail template and apply it to at least the most recent 30 videos before the campaign.',
  );
  set(
    'C3',
    'critical',
    'The three most-viewed videos all use a bold, high-contrast title treatment with a single clear subject; the most recent uploads abandon that for busy, low-contrast layouts. The channel is drifting away from the exact pattern that drives its best performers.',
    'Apply the winning thumbnail pattern from the top performers to all campaign-period uploads.',
  );
  set(
    'D1',
    'conversion',
    'Several recent titles bury the topic past the first 40 characters, so the hook is cut off in browse and search views.',
    'Rewrite recent titles to front-load the benefit or keyword within the first ~40 characters.',
  );
  set(
    'E1',
    'housekeeping',
    'Upload cadence has stretched from weekly to roughly every 2–3 weeks over the past quarter, which weakens the "active channel" signal a new subscriber looks for.',
    'Commit to a steady weekly cadence through the campaign window (ongoing, likely a separate retainer).',
  );
  return a;
}

export { uid };
